import { FormikProps } from "formik";
import {
  ButtonsBloc,
  FGListen,
  FGSelectInput,
  FGTextAreaInput,
  FieldGroup,
  FieldSet,
  InlineButtonContainer,
} from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import * as Yup from "yup";

import {
  AffixRequestApi,
  ERequestState,
  KennelApi,
  KennelExportRequestDto,
  KennelImportRequestDto,
  PersonApi,
  RequestApi,
  RequestDto,
  RequestInfoDto,
} from "../../../../../api";
import {
  CancelButton,
  FGCountrySelect,
  FGPeopleSearchInput,
  FGSRSHDateMaskInput,
  SharepointButton,
  SmallFormGenerator,
} from "../../../../../components";
import { useDialog, useEventsContext } from "../../../../../contexts";
import { ERoutes } from "../../../../../ERoutes";
import { useApiService, useCrudApi, useTl, useWindow } from "../../../../../hooks";
import { useReferential } from "../../../../../hooks/useReferential";
import { ETLCodes } from "../../../../../locales";

export interface IKennelExportRequestDetailProps {
  kennelExportRequestId: number;
  editMode: boolean;
  requestInfo: RequestInfoDto;
}

export const KennelExportRequestDetail: React.FunctionComponent<IKennelExportRequestDetailProps> = ({
  kennelExportRequestId,
  editMode,
}) => {
  const { t } = useTl();
  const history = useHistory();
  const api = useApiService(AffixRequestApi);
  const personApi = useApiService(PersonApi);
  const kennelApi = useApiService(KennelApi);
  const { openNewTab } = useWindow();
  const { showDialogPromise } = useDialog();

  const [kennels, kloading] = useReferential((a) => a.getOpenedKennels(0), true, []);

  // =========================== DATA MANIPULATION ==================================================

  const { data, loading, saving, saveItem, refresh } = useCrudApi<KennelExportRequestDto>(
    React.useMemo(
      () => ({
        getApiFn: (id = kennelExportRequestId) =>
          id > 0
            ? api.getExportKennelRequest(id)
            : new KennelExportRequestDto({
                request: new RequestDto({
                  status: ERequestState.REQUESTED,
                  remark: "",
                  requestDate: new Date(),
                }),
              }).toJSON(),
        saveApiFn: (d: KennelImportRequestDto) => api.saveKennelExportRequest(d),
        onSavedRoute: (d) => `${ERoutes.affixRequest}/${d.kennelExportRequestId}/detail/edit/kennelExport`,
      }),
      [api, kennelExportRequestId]
    ),
    [kennelExportRequestId]
  );

  const [saveChecking, setSaveChecking] = React.useState(false);
  const saveCheck = React.useCallback(
    async (values) => {
      setSaveChecking(true);
      let isBanned = false;
      if (kennelExportRequestId <= 0) {
        isBanned = (await personApi.isPersonExcludedQuery(values?.request?.personId)).value;
      }
      if (isBanned) {
        const isbannedValidation = await showDialogPromise({
          message: t(ETLCodes.PersonIsBannedConfirmMessage),
          title: t(ETLCodes.Warning),
        });
        if (isbannedValidation !== "yes") return;
      }
      setSaveChecking(false);
      await saveItem(values);
    },
    [kennelExportRequestId, personApi, saveItem, showDialogPromise, t]
  );

  // =========================== EVENT MANAGER ==================================================
  const onRequestRefreshEvent = React.useCallback(() => {
    refresh();
  }, [refresh]);

  const { subscribeToEvent, unsubscribeEvent } = useEventsContext();
  React.useEffect(() => {
    subscribeToEvent("RequestRefresh", onRequestRefreshEvent);
    return () => unsubscribeEvent("RequestRefresh", onRequestRefreshEvent);
  }, [onRequestRefreshEvent, subscribeToEvent, unsubscribeEvent]);

  // =========================== FORM ==================================================
  const RequestedFormSchema = React.useMemo(() => {
    return Yup.object().shape({
      kennelId: Yup.number()
        .nullable()
        .required(t(ETLCodes.Required))
        .test("is-kennel-belgian", t(ETLCodes.KennelisNotInBelgium), async function (value) {
          if (!value || value < 0) return true;
          const res = await kennelApi.isKennelBelgian(value);
          return res.value;
        }),
      countryId: Yup.number().nullable().required(t(ETLCodes.Required)),
      request: Yup.object().shape({
        personId: Yup.number()
          .nullable()
          .required(t(ETLCodes.Required))
          .test("missing-adress-information", t(ETLCodes.MissingCompleteAdressOrEmail), async function (value) {
            if (kennelExportRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
              if (value !== 0 && value !== null && value !== undefined) {
                const result = await personApi.missingAddressInformationCheck(value);
                return result.value;
              }
              return true;
            }
            return true;
          }),
        requestDate: Yup.date().nullable().required(t(ETLCodes.Required)),
      }),
    });
  }, [data, kennelApi, kennelExportRequestId, personApi, t]);

  const OtherFormSchema = React.useMemo(() => {
    return Yup.object().shape({});
  }, []);

  const statusEditable = React.useMemo(() => {
    if (data) {
      return (
        data?.request?.status === ERequestState.REQUESTED ||
        data?.request?.status === ERequestState.AWAITING_APPLICANT_REACTION ||
        data?.request?.status === ERequestState.BILLED ||
        data?.request?.status === ERequestState.PAID
      );
    }
    return false;
  }, [data]);

  const requestApi = useApiService(RequestApi);
  const [shpLoading, setShpLoading] = React.useState(false);
  const existsInShp = React.useMemo(() => !!data?.request.sharepointUri, [data?.request]);
  const openOrCreateInShp = React.useCallback(async () => {
    setShpLoading(true);
    if (!!data?.request.sharepointUri) {
      window.open(data?.request.sharepointUri, "_blank");
      setShpLoading(false);
    } else {
      try {
        await requestApi.createInSharepoint(data?.requestId);
        refresh();
      } catch (e) {
        throw e;
      } finally {
        setShpLoading(false);
      }
    }
  }, [data?.request.sharepointUri, data?.requestId, requestApi, refresh]);

  const formikRef = React.useRef<FormikProps<KennelExportRequestDto>>();

  return (
    <>
      <SmallFormGenerator
        initialValues={data}
        editMode={editMode}
        onSubmit={saveCheck}
        validationSchema={data?.request?.status === ERequestState.REQUESTED ? RequestedFormSchema : OtherFormSchema}
        loading={loading}
        onCancel={() => history.push(ERoutes.affixRequest)}
        saving={saving || saveChecking}
        hideButtons={data?.request?.status === ERequestState.EXECUTED}
        formikInnerRef={(f) => (formikRef.current = f)}
      >
        <FieldSet
          title={t(ETLCodes.KennelExportRequest)}
          rightElement={
            kennelExportRequestId > 0 && (
              <SharepointButton existsInShp={existsInShp} loading={shpLoading} onClick={openOrCreateInShp} />
            )
          }
        >
          <FieldGroup columns={2}>
            <div>
              <FGSRSHDateMaskInput
                name="request.requestDate"
                label={t(ETLCodes.RequestDate)}
                requiredMark
                readonly={!statusEditable || data?.request?.isBlocked}
              />
              <FGSelectInput
                name="kennelId"
                label={t(ETLCodes.Kennel)}
                items={kennels}
                loading={kloading}
                onItemViewClick={(item) => openNewTab(`${ERoutes.kennel}/${item}/detail/view`)}
                readonly={!statusEditable || data?.request?.isBlocked}
                requiredMark
              />
              <FGPeopleSearchInput
                name="request.personId"
                label={t(ETLCodes.Contact)}
                onItemViewClick={(item) => openNewTab(`${ERoutes.people}/${item}/detail/view`)}
                readonly={!statusEditable || data?.request?.isBlocked}
                showWarning={true}
                kennelId={formikRef.current?.values?.kennelId}
                requiredMark
              />
              <FGCountrySelect
                name="countryId"
                label={t(ETLCodes.Country)}
                includeBelgium={false}
                onItemViewClick={(item) => openNewTab(`${ERoutes.country}/${item}/detail/view`)}
                readonly={!statusEditable || data?.request?.isBlocked}
                requiredMark
              />
            </div>
            <FGTextAreaInput label={t(ETLCodes.Remark)} name="request.remark" growVertically rows={6} />
          </FieldGroup>
        </FieldSet>

        {data?.request?.status === ERequestState.EXECUTED && (
          <InlineButtonContainer>
            <div />
            <ButtonsBloc style={{ float: "right" }}>
              <CancelButton minimal={false} onClick={() => history.push(`${ERoutes.affixRequest}`)} />
            </ButtonsBloc>
          </InlineButtonContainer>
        )}
      </SmallFormGenerator>
    </>
  );
};
